import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Data,
  Router,
  RouterStateSnapshot,
  UrlSerializer,
  UrlTree
} from "@angular/router";
import { Observable } from "rxjs";
import { first, map, tap } from "rxjs/operators";

import { AuthService } from "./auth.service";

// TODO: use string literal types instead of just a string in the future

// TODO: this cannot be used with AOT (cannot do computed object property names)
export const AUTH_GUARD_PERMISSION: string = "AUTH_GUARD_PERMISSION";
export const AUTH_GUARD_REDIRECT: string = "AUTH_GUARD_REDIRECT";

export type AuthGuardRedirectUrl = string | undefined;
export type AuthGuardRedirectUrlBuilder = (
  auth: AuthService,
  targetRoute: string
) => AuthGuardRedirectUrl;
export type AuthGuardRedirect =
  | AuthGuardRedirectUrl
  | AuthGuardRedirectUrlBuilder;

@Injectable()
export class AuthGuard implements CanActivate, CanActivateChild {
  private windowRef: Window;

  constructor(
    private authService: AuthService,
    private router: Router,
    private urlSerializer: UrlSerializer,
    @Inject(DOCUMENT) documentRef: any
  ) {
    this.windowRef = (documentRef as Document).defaultView!;
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.hasPermission(route.data).pipe(
      tap((isAllowed) => {
        if (isAllowed) {
          return;
        }

        let redirectUrl: string | undefined = this.getRedirectUrl(route, state);
        if (redirectUrl != null) {
          if (redirectUrl.startsWith("url:")) {
            this.windowRef.location.replace(redirectUrl.substr(4));
          } else {
            this.router.navigateByUrl(redirectUrl);
          }
        }
      })
    );
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.canActivate(route, state);
  }

  private hasPermission(routeData: Data): Observable<boolean> {
    // TODO: handle string parsing better, use library
    return this.authService.onInitialized.pipe(
      first(),
      map(() => {
        const permission = routeData[AUTH_GUARD_PERMISSION];
        return this.authService.hasPermission(permission);
      })
    );
  }

  private getRedirectUrl(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): string | undefined {
    let redirect = route.data[AUTH_GUARD_REDIRECT];
    if (redirect == null) {
      return;
    }

    let targetRoute;
    if (state.root.queryParams.redirect != null) {
      targetRoute = state.root.queryParams.redirect;
    } else {
      targetRoute = state.url;
    }

    if (typeof redirect === "function") {
      return redirect(this.authService, targetRoute);
    }

    if (typeof redirect === "string" && redirect.startsWith("url:")) {
      return redirect;
    }

    // append target route to redirect query param
    let redirectTree: UrlTree = this.urlSerializer.parse(redirect);
    redirectTree.queryParams.redirect = targetRoute;
    redirect = this.urlSerializer.serialize(redirectTree);

    return redirect;
  }
}
